import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Play, History, ChevronDown, ChevronUp } from 'lucide-react';
import {
  listSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  triggerSchedule,
  listScheduleRuns,
  buildCron,
  describeCron,
  TASK_TYPE_LABELS,
  RUN_STATUS_LABELS,
  type AgentSchedule,
  type AgentScheduleRun,
  type SchedulePreset,
} from '../api/agentSchedules';
import { useToast } from './ui/toastContext';
import { useConfirm } from './ui/useConfirm';

const getApiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

const WEEKDAY_OPTIONS = ['日', '一', '二', '三', '四', '五', '六'];

/** Agent 定时任务面板（P8）：任务列表 + 新建表单（档位 / 高级 cron）+ 运行历史。 */
export default function SchedulePanel({ agentId }: { agentId: string }) {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [schedules, setSchedules] = useState<AgentSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedRuns, setExpandedRuns] = useState<Record<string, AgentScheduleRun[]>>({});

  // 新建表单状态
  const [taskType, setTaskType] = useState('eval_regression');
  const [preset, setPreset] = useState<SchedulePreset>('daily');
  const [time, setTime] = useState('09:00');
  const [weekday, setWeekday] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [customCron, setCustomCron] = useState('0 9 * * *');
  const [artifactId, setArtifactId] = useState('');
  const [suiteId, setSuiteId] = useState('');
  const [runtimeId, setRuntimeId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSchedules(await listSchedules(agentId));
    } catch (e) {
      toast.error('加载定时任务失败: ' + getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    const tick = () => void load();
    tick();
  }, [load]);

  const effectiveCron = () => (preset === 'custom' ? customCron.trim() : buildCron(preset, time, weekday, dayOfMonth));

  const handleCreate = async () => {
    const payload: Record<string, unknown> = {};
    if (taskType === 'eval_regression') {
      if (!artifactId.trim()) {
        toast.error('评测回归任务需要填写产物 ID');
        return;
      }
      payload.artifact_id = artifactId.trim();
      if (suiteId.trim()) payload.suite_id = suiteId.trim();
    } else if (taskType === 'ct_feedback_collect' && runtimeId.trim()) {
      payload.runtime_id = runtimeId.trim();
    }
    try {
      await createSchedule(agentId, { task_type: taskType, cron: effectiveCron(), payload });
      setShowForm(false);
      setArtifactId('');
      setSuiteId('');
      setRuntimeId('');
      await load();
      toast.success('定时任务已创建');
    } catch (e) {
      toast.error('创建失败: ' + getApiErrorMessage(e));
    }
  };

  const handleToggle = async (s: AgentSchedule) => {
    try {
      await updateSchedule(s.id, { enabled: !s.enabled });
      await load();
      toast.success(s.enabled ? '已禁用' : '已启用');
    } catch (e) {
      toast.error('更新失败: ' + getApiErrorMessage(e));
    }
  };

  const handleTrigger = async (s: AgentSchedule) => {
    try {
      await triggerSchedule(s.id);
      toast.success('已触发一次运行，稍后可在运行记录查看结果');
      if (expandedRuns[s.id]) await toggleRuns(s, true);
    } catch (e) {
      toast.error('触发失败: ' + getApiErrorMessage(e));
    }
  };

  const handleDelete = async (s: AgentSchedule) => {
    if (!(await confirm({ title: '确定删除该定时任务？', message: '其运行记录将一并删除。', danger: true, confirmText: '删除' }))) return;
    try {
      await deleteSchedule(s.id);
      await load();
      toast.success('已删除');
    } catch (e) {
      toast.error('删除失败: ' + getApiErrorMessage(e));
    }
  };

  const toggleRuns = async (s: AgentSchedule, forceRefresh = false) => {
    if (expandedRuns[s.id] && !forceRefresh) {
      setExpandedRuns((prev) => {
        const next = { ...prev };
        delete next[s.id];
        return next;
      });
      return;
    }
    try {
      const runs = await listScheduleRuns(s.id);
      setExpandedRuns((prev) => ({ ...prev, [s.id]: runs }));
    } catch (e) {
      toast.error('加载运行记录失败: ' + getApiErrorMessage(e));
    }
  };

  const runStatusBadge = (status: string) => (
    <span className={`badge ${status === 'success' ? 'success' : status === 'failed' ? 'error' : 'info'}`}>
      {RUN_STATUS_LABELS[status] || status}
    </span>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 style={{ margin: 0 }}>定时任务</h3>
        <button
          className="primary"
          style={{ padding: '6px 12px', fontSize: 13 }}
          onClick={() => setShowForm(!showForm)}
        >
          <Plus size={14} /> 新建任务
        </button>
      </div>

      {showForm && (
        <div className="card mb-3" style={{ background: 'var(--bg-primary)' }}>
          <h4 className="mb-2">新建定时任务</h4>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <label style={{ width: 80, fontSize: 13 }} className="text-muted">任务类型</label>
              <select value={taskType} onChange={(e) => setTaskType(e.target.value)} style={{ flex: 1 }}>
                {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 items-center">
              <label style={{ width: 80, fontSize: 13 }} className="text-muted">调度档位</label>
              <select value={preset} onChange={(e) => setPreset(e.target.value as SchedulePreset)} style={{ flex: 1 }}>
                <option value="daily">每天</option>
                <option value="weekly">每周</option>
                <option value="monthly">每月</option>
                <option value="custom">高级（cron 表达式）</option>
              </select>
            </div>
            {preset !== 'custom' && (
              <div className="flex gap-2 items-center">
                <label style={{ width: 80, fontSize: 13 }} className="text-muted">时间</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                {preset === 'weekly' && (
                  <select value={weekday} onChange={(e) => setWeekday(parseInt(e.target.value, 10))}>
                    {WEEKDAY_OPTIONS.map((label, idx) => (
                      <option key={idx} value={idx}>周{label}</option>
                    ))}
                  </select>
                )}
                {preset === 'monthly' && (
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                    style={{ width: 80 }}
                    title="每月几号"
                  />
                )}
              </div>
            )}
            {preset === 'custom' && (
              <div className="flex gap-2 items-center">
                <label style={{ width: 80, fontSize: 13 }} className="text-muted">cron</label>
                <input
                  type="text"
                  value={customCron}
                  onChange={(e) => setCustomCron(e.target.value)}
                  placeholder="分 时 日 月 星期，如 0 9 * * *"
                  style={{ flex: 1, fontFamily: 'monospace' }}
                />
              </div>
            )}
            <div className="text-muted" style={{ fontSize: 12 }}>
              将按 cron 表达式 <code>{effectiveCron()}</code>（UTC）调度：{describeCron(effectiveCron())}
            </div>
            {taskType === 'eval_regression' && (
              <>
                <div className="flex gap-2 items-center">
                  <label style={{ width: 80, fontSize: 13 }} className="text-muted">产物 ID</label>
                  <input
                    type="text"
                    value={artifactId}
                    onChange={(e) => setArtifactId(e.target.value)}
                    placeholder="被评测的 Artifact ID"
                    style={{ flex: 1, fontFamily: 'monospace' }}
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <label style={{ width: 80, fontSize: 13 }} className="text-muted">评测集 ID</label>
                  <input
                    type="text"
                    value={suiteId}
                    onChange={(e) => setSuiteId(e.target.value)}
                    placeholder="suite_id（内置套件或 manifest 引用）"
                    style={{ flex: 1, fontFamily: 'monospace' }}
                  />
                </div>
              </>
            )}
            {taskType === 'ct_feedback_collect' && (
              <div className="flex gap-2 items-center">
                <label style={{ width: 80, fontSize: 13 }} className="text-muted">运行时 ID</label>
                <input
                  type="text"
                  value={runtimeId}
                  onChange={(e) => setRuntimeId(e.target.value)}
                  placeholder="可选；留空则采集该 Agent 最近的 CT 运行时"
                  style={{ flex: 1, fontFamily: 'monospace' }}
                />
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <button className="primary" onClick={handleCreate}>创建</button>
              <button className="secondary" onClick={() => setShowForm(false)}>取消</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted">加载中...</p>
      ) : schedules.length === 0 ? (
        <p className="text-muted">暂无定时任务，点击右上角新建</p>
      ) : (
        <div className="list">
          {schedules.map((s) => (
            <div key={s.id}>
              <div className="list-item">
                <div className="list-item-content">
                  <div className="flex items-center gap-2">
                    <strong>{TASK_TYPE_LABELS[s.task_type] || s.task_type}</strong>
                    <span className={`badge ${s.enabled ? 'success' : 'info'}`}>{s.enabled ? '启用' : '禁用'}</span>
                  </div>
                  <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
                    调度: {describeCron(s.cron)}
                    {describeCron(s.cron) !== s.cron && <code style={{ marginLeft: 6, fontSize: 12 }}>{s.cron}</code>}
                    {s.last_run_at && ` · 上次运行: ${new Date(s.last_run_at).toLocaleString()}`}
                    {s.enabled && s.next_run_at && ` · 下次: ${new Date(s.next_run_at).toLocaleString()}`}
                  </div>
                </div>
                <div className="list-item-actions">
                  <button
                    className="secondary"
                    style={{ padding: '6px 10px' }}
                    title="立即运行一次"
                    onClick={() => handleTrigger(s)}
                  >
                    <Play size={14} />
                  </button>
                  <button
                    className={s.enabled ? 'danger' : 'primary'}
                    style={{ padding: '6px 10px', fontSize: 12 }}
                    onClick={() => handleToggle(s)}
                  >
                    {s.enabled ? '禁用' : '启用'}
                  </button>
                  <button
                    className="secondary"
                    style={{ padding: '6px 10px' }}
                    title="运行记录"
                    onClick={() => toggleRuns(s)}
                  >
                    {expandedRuns[s.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  <button className="danger" style={{ padding: '6px 10px' }} title="删除" onClick={() => handleDelete(s)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {expandedRuns[s.id] && (
                <div className="card" style={{ background: 'var(--bg-primary)', marginTop: 4, marginBottom: 8 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <History size={14} />
                    <strong style={{ fontSize: 13 }}>运行记录</strong>
                  </div>
                  {expandedRuns[s.id].length === 0 ? (
                    <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>暂无运行记录</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {expandedRuns[s.id].map((run) => (
                        <div key={run.id} style={{ fontSize: 13 }}>
                          <div className="flex items-center gap-2">
                            {runStatusBadge(run.status)}
                            <span className="text-muted">
                              {run.trigger === 'manual' ? '手动' : '定时'}
                              {run.started_at && ` · ${new Date(run.started_at).toLocaleString()}`}
                            </span>
                          </div>
                          {run.error && (
                            <div style={{ color: 'var(--color-error)', marginTop: 2 }}>{run.error}</div>
                          )}
                          {run.result_summary && (
                            <div className="text-muted" style={{ marginTop: 2, fontFamily: 'monospace', fontSize: 12 }}>
                              {JSON.stringify(run.result_summary)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {dialog}
    </div>
  );
}
